import axios from 'axios';

/* ------------------------------------------------------------------ *
 * Base URL resolution
 *
 * REACT_APP_API_URL is the canonical variable. It may be given with or
 * without the trailing "/api" — both are normalised to end in "/api".
 * REACT_APP_BACKEND_URL is kept as a deprecated fallback so existing
 * deployments keep working; prefer REACT_APP_API_URL for new setups.
 * ------------------------------------------------------------------ */

const normalizeApiBaseUrl = (url) => {
  if (!url) return null;
  const trimmed = url.toString().trim().replace(/\/+$/, '');
  if (!trimmed) return null;
  return /\/api$/i.test(trimmed) ? trimmed : `${trimmed}/api`;
};

export const getApiBaseUrl = () => {
  const configured = normalizeApiBaseUrl(
    process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL
  );
  if (configured) return configured;

  // No env var configured. In local development talk to the dev backend
  // directly; anywhere else default to your production backend Vercel URL.
  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:5000/api';
    }
    return 'https://max-frontend-gdep.vercel.app/api';
  }

  return 'https://max-frontend-gdep.vercel.app/api';
};

export const TOKEN_STORAGE_KEY = 'protech_token';
export const USER_STORAGE_KEY = 'protech_user';

const API = axios.create({ baseURL: getApiBaseUrl() });

API.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/* ------------------------------------------------------------------ *
 * Error normalisation
 *
 * Every rejection reaching a component carries a predictable shape:
 *   err.status   -> HTTP status (0 when the request never reached us)
 *   err.message  -> safe, human-readable message
 *   err.errors   -> optional field-level errors from the backend
 * The original axios error stays available on err.original.
 * ------------------------------------------------------------------ */

export const normalizeApiError = (error) => {
  if (error?.isApiError) return error;

  const status = error?.response?.status ?? 0;
  const data = error?.response?.data;

  let message;
  if (data && typeof data === 'object' && typeof data.message === 'string') {
    message = data.message;
  } else if (typeof data === 'string' && data.trim() && !data.trim().startsWith('<')) {
    message = data.trim();
  }

  if (!message) {
    if (status === 0) {
      message = 'Cannot reach the server. Please check your connection and try again.';
    } else if (status === 401) {
      message = 'Your session has expired. Please sign in again.';
    } else if (status === 403) {
      message = 'You do not have permission to perform this action.';
    } else if (status === 404) {
      message = 'The requested resource was not found.';
    } else if (status === 429) {
      message = 'Too many requests. Please wait a moment and try again.';
    } else if (status >= 500) {
      message = 'Something went wrong on our end. Please try again shortly.';
    } else {
      message = 'Request failed. Please try again.';
    }
  }

  const normalized = new Error(message);
  normalized.isApiError = true;
  normalized.status = status;
  normalized.errors = (data && typeof data === 'object' && data.errors) || null;
  normalized.original = error;
  // Preserved so existing `err.response?.data?.message` reads keep working.
  normalized.response = error?.response;
  return normalized;
};

API.interceptors.response.use(
  (response) => {
    if (typeof response.data === 'string' && response.data.trim().startsWith('<')) {
      const error = new Error('Cannot reach API server. Received HTML fallback instead of JSON response.');
      error.response = response;
      return Promise.reject(normalizeApiError(error));
    }
    return response;
  },
  (error) => Promise.reject(normalizeApiError(error))
);

/* ------------------------------------------------------------------ *
 * API contract
 * Each wrapper below maps 1:1 onto a real backend route.
 * ------------------------------------------------------------------ */

export const authAPI = {
  // POST /api/auth/register       { name, email, password }
  register: (data) => API.post('/auth/register', data),
  // POST /api/auth/login          { email, password }
  login: (data) => API.post('/auth/login', data),
  // GET  /api/auth/me             (protected)
  getMe: () => API.get('/auth/me'),
  // POST /api/auth/verify-otp     { email, otp, type }
  verifyOTP: (data) => API.post('/auth/verify-otp', data),
  // POST /api/auth/resend-otp     { email, type }
  resendOTP: (data) => API.post('/auth/resend-otp', data),
  // POST /api/auth/forgot-password { email }
  forgotPassword: (data) => API.post('/auth/forgot-password', data),
  // POST /api/auth/reset-password  { email, otp, newPassword }
  resetPassword: (data) => API.post('/auth/reset-password', data),
  // PUT  /api/auth/change-password { currentPassword, newPassword } (protected)
  changePassword: (data) => API.put('/auth/change-password', data),
};

export const productAPI = {
  // GET /api/products?page&limit&sort&category&search&minPrice&maxPrice&inStock&featured
  getAll: (params) => API.get('/products', { params }),
  // GET /api/products/featured
  getFeatured: () => API.get('/products/featured'),
  // GET /api/products/categories
  getCategories: () => API.get('/products/categories'),
  // GET /api/products/suggestions?q=
  getSuggestions: (q) => API.get('/products/suggestions', { params: { q } }),
  // GET /api/products/:id
  getById: (id) => API.get(`/products/${id}`),
  // GET /api/products/:id/related
  getRelated: (id) => API.get(`/products/${id}/related`),
  // POST /api/products/:id/reviews { rating, comment } (protected)
  createReview: (id, data) => API.post(`/products/${id}/reviews`, data),
};

export const userAPI = {
  // GET /api/users/profile (protected)
  getProfile: () => API.get('/users/profile'),
  // PUT /api/users/profile { name, phone, address } (protected)
  updateProfile: (data) => API.put('/users/profile', data),
  // PUT /api/users/profile/avatar  multipart field "avatar" (protected)
  uploadAvatar: (formData) =>
    API.put('/users/profile/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  // GET /api/users/wishlist (protected)
  getWishlist: () => API.get('/users/wishlist'),
  // POST /api/users/wishlist/:productId (protected)
  toggleWishlist: (productId) => API.post(`/users/wishlist/${productId}`),
};

export const orderAPI = {
  // POST /api/orders { orderItems, shippingAddress, paymentMethod } (protected)
  create: (data) => API.post('/orders', data),
  // GET /api/orders/my-orders?page&limit (protected)
  getMyOrders: (params) => API.get('/orders/my-orders', { params }),
  // GET /api/orders/:id (protected)
  getById: (id) => API.get(`/orders/${id}`),
  // PUT /api/orders/:id/pay { razorpayOrderId, razorpayPaymentId, razorpaySignature }
  markPaid: (id, data) => API.put(`/orders/${id}/pay`, data),
};

export const paymentAPI = {
  // GET /api/payment/key (protected)
  getKey: () => API.get('/payment/key'),
  // POST /api/payment/create-order { orderId } (protected)
  // Amount is derived server-side from the database order — never sent by the client.
  createRazorpayOrder: (data) => API.post('/payment/create-order', data),
  // POST /api/payment/verify { orderId, razorpayOrderId, razorpayPaymentId, razorpaySignature }
  verify: (data) => API.post('/payment/verify', data),
};

export const adminAPI = {
  // GET /api/admin/dashboard
  getDashboard: () => API.get('/admin/dashboard'),
  // GET /api/admin/analytics
  getAnalytics: () => API.get('/admin/analytics'),
  // GET /api/admin/products?page&limit&search&includeInactive
  getProducts: (params) => API.get('/admin/products', { params }),
  // POST /api/admin/products  multipart field "images"
  createProduct: (formData) =>
    API.post('/admin/products', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  // PUT /api/admin/products/:id  multipart field "images"
  updateProduct: (id, formData) =>
    API.put(`/admin/products/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  // DELETE /api/admin/products/:id  (soft delete / archive)
  deleteProduct: (id) => API.delete(`/admin/products/${id}`),
  // PUT /api/admin/products/:id/toggle-stock { restoreQty }
  toggleProductStock: (id, restoreQty) =>
    API.put(`/admin/products/${id}/toggle-stock`, restoreQty ? { restoreQty } : {}),
  // GET /api/admin/orders?page&limit&status&isPaid
  getOrders: (params) => API.get('/admin/orders', { params }),
  // PUT /api/admin/orders/:id/status { status, trackingNumber, note }
  updateOrderStatus: (id, data) => API.put(`/admin/orders/${id}/status`, data),
  // GET /api/admin/users?page&limit&search&role
  getUsers: (params) => API.get('/admin/users', { params }),
  // PUT /api/admin/users/:id { isActive, role }
  updateUser: (id, data) => API.put(`/admin/users/${id}`, data),
};

export default API;
